import {
  ChangeEvent,
  HTMLProps,
  useEffect,
  useRef,
  KeyboardEvent,
  useCallback,
  useState,
} from "react";
import { CommandButton, CommandButtonGroup } from "@remirror/react-ui";
import { useAttrs } from "@remirror/react";
import { useLinkState } from "../../hooks/useLinkState";
import "./LinkDialog.css";

type Props = {
  nodeId: string;
};

/* Based on https://github.com/remirror/remirror/blob/main/packages/remirror__extension-link/src/link-extension.ts#L52-L54
   Matches Remirror's autolink logic since we have that feature turned on */
const DEFAULT_AUTO_LINK_REGEX =
  /(?:(?:(?:https?|ftp):)?\/\/)?(?:\S+(?::\S*)?@)?(?:(?:[\da-z\u00A1-\uFFFF][\w\u00A1-\uFFFF-]{0,62})?[\da-z\u00A1-\uFFFF]\.)*(?:(?:\d(?!\.)|[a-z\u00A1-\uFFFF])(?:[\da-z\u00A1-\uFFFF][\w\u00A1-\uFFFF-]{0,62})?[\da-z\u00A1-\uFFFF]\.)+[a-z\u00A1-\uFFFF]{2,}(?::\d{2,5})?(?:[#/?]\S*)?/i;

const isValidUrl = (url: string) => {
  if (url === "") return true; // We want users to be able to remove the link completely

  return DEFAULT_AUTO_LINK_REGEX.test(url);
};

const DelayAutoFocusInput = (
  props: HTMLProps<HTMLInputElement> & { isValid: boolean; nodeId: string },
) => {
  const inputRef = useRef<HTMLInputElement>(null);

  // React does not recognize the `nodeId` or `isValid` props on a DOM element.
  const { nodeId, isValid, ...rest } = props;
  const { onRemove } = useLinkState(nodeId);
  const url = (useAttrs().link()?.href as string) ?? "";

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
        inputRef.current.scrollLeft = 0; // Ensure the input scrolls to the beginning
      }
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, []);

  const openLink = useCallback(() => {
    window.open(url, "_blank", "noopener,noreferrer");
  }, [url]);

  return (
    <div className="link-input-wrapper">
      <input
        className={`link-input ${isValid ? "" : "error"}`}
        ref={inputRef}
        {...rest}
      />
      {url && (
        <CommandButtonGroup>
          <CommandButton
            aria-label="Remove link"
            commandName="removeLink"
            onSelect={onRemove}
            icon="linkUnlink"
            enabled
          />
          <CommandButton
            aria-label="Open link"
            commandName="openLink"
            onSelect={openLink}
            icon="externalLinkFill"
            enabled
          />
        </CommandButtonGroup>
      )}
    </div>
  );
};

export function LinkDialog({ nodeId }: Props) {
  const { setHref, href, submitHref, cancelHref } = useLinkState(nodeId);
  const [isValid, setIsValid] = useState(true);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setHref(event.target.value);
      setIsValid(true);
    },
    [setHref],
  );

  return (
    <DelayAutoFocusInput
      nodeId={nodeId}
      placeholder="Type or paste link..."
      onChange={handleChange}
      value={href}
      isValid={isValid}
      onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
        const { code } = event;

        if (code === "Enter") {
          const urlIsValid = isValidUrl(href);
          if (urlIsValid) {
            submitHref();
          } else {
            setIsValid(false);
          }
        }

        if (code === "Escape") {
          cancelHref();
        }
      }}
    />
  );
}
